import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { NullablePriceAmount, PriceAmountWithDetails } from '@/api';
import { PriceWithDetailsTooltipWithConversions } from '@/components/PriceWithDetails/PriceWithDetails';
import WorkshopPriceLabel from '@/components/WorkshopPriceLabel/WorkshopPriceLabel';
import Button from '@/design_system/Button';
import { InputMoney } from '@/design_system/InputNumber';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateArticleActionSparePart } from '@/models/articleActionSparePart';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import {
  getBasePrice,
  getPrice,
  getPriceLabel,
  getShowAmountBeforeTaxes,
} from '@/utils/articleActionSpareParts';

import { MarginTooltip } from '../MarginTooltip';

export const ActionSparePartPrice = ({
  sparePart,
  priceType,
  actionId,
  editable = false,
  disabled = false,
  textSize = 'medium',
  previousSparePart,
}: {
  sparePart: ArticleActionWithRelations['spareParts'][number];
  priceType: 'workshop' | 'organization';
  actionId: string;
  editable?: boolean;
  disabled?: boolean;
  textSize?: 'medium' | 'small';
  previousSparePart?: ArticleActionWithRelations['spareParts'][number];
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { article, request, state, errors } = useArticleContext();
  const { view: requestView } = useRequestContext();

  const sparePartErrors = errors.services?.actions?.actions
    ?.find((action) => action.id === actionId)
    ?.spareParts?.find((sparePartError) => sparePartError.id === sparePart.id);

  const hasMissingPriceError =
    priceType === 'organization'
      ? sparePartErrors?.missingOrganizationPrice
      : sparePartErrors?.missingWorkshopPrice;

  const hasIsUpdatingError =
    priceType === 'organization'
      ? sparePartErrors?.organizationPriceUpdateInProgress
      : sparePartErrors?.workshopPriceUpdateInProgress;

  const isEditing = state.priceUpdates.updatesInProgress.some(
    (priceUpdate) => priceUpdate.sparePartId === sparePart.id && priceUpdate.type === priceType
  );

  const price = getPrice(priceType, sparePart, article.workshopId);
  const showAmountBeforeTaxes = getShowAmountBeforeTaxes(priceType, requestView);
  const label = getPriceLabel(priceType, requestView);

  const edited = previousSparePart
    ? getPrice(priceType, previousSparePart, article.workshopId)?.amount !== price?.amount
    : false;

  const { mutate: updateSparePart, isPending } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId,
    sparePartId: sparePart.id,
    onSettled: () => {
      state.priceUpdates.setUpdatesInProgress((prevState) =>
        prevState.filter((update) => update.sparePartId !== sparePart.id)
      );
    },
  });
  const updateField = priceType === 'workshop' ? 'customWorkshopPrice' : 'customOrganizationPrice';
  const updateAmountField = showAmountBeforeTaxes ? 'amountBeforeTaxes' : 'amount';

  return (
    <Stack row gap="4px" alignItems="center" ariaLabel={label}>
      <InlineText
        size={textSize === 'small' ? '300' : '200'}
        color={
          hasMissingPriceError || hasIsUpdatingError
            ? 'danger'
            : disabled
              ? 'disabled'
              : 'secondary'
        }
        nowrap
      >
        {label}/
        {sparePart.sparePartType.unit ? (
          _db(sparePart.sparePartType.unit)
        ) : (
          <Trans id="spare-parts-table.column.reference.part">part</Trans>
        )}
        :
      </InlineText>
      {!isEditing ? (
        <Stack row gap="4px" alignItems="center" flexWrap="nowrap">
          <Stack row gap="8px" alignItems="center" flexWrap="nowrap">
            <ActionSparePartReadPrice
              showAmountBeforeTaxes={showAmountBeforeTaxes}
              price={price}
              disabled={disabled}
              textSize={textSize}
              sparePart={sparePart}
              edited={edited}
              hasError={!!hasMissingPriceError}
            />
            {priceType === 'organization' && (
              <InlineText size="200" color="secondary">
                {requestView?.organizationPrice?.showAmountBeforeTaxes
                  ? `(${t({ id: '_general.excl-vat', message: 'excl. VAT' })})`
                  : `(${t({ id: '_general.incl-vat', message: 'incl. VAT' })})`}
              </InlineText>
            )}
            {priceType === 'organization' && (
              <MarginTooltip customOrganizationPrice={sparePart?.customOrganizationPrice} />
            )}
            {priceType === 'workshop' && (
              <WorkshopPriceLabel hideLabel noVATApplied={price?.noVATApplied} size="200" />
            )}
            {editable && !disabled && (
              <Button
                ariaLabel={t({
                  id: 'spare-parts-table.price.edit',
                  message: 'Edit',
                })}
                tooltip={t({
                  id: 'spare-parts-table.price.edit',
                  message: 'Edit',
                })}
                size="small"
                variant="secondary"
                onPress={() => {
                  state.priceUpdates.setUpdatesInProgress((prevState) => [
                    ...prevState,
                    { actionId, type: priceType, sparePartId: sparePart.id },
                  ]);
                }}
                iconOnly
              >
                <IconEdit />
              </Button>
            )}
          </Stack>
        </Stack>
      ) : (
        <ActionSparePartEditPrice
          showAmountBeforeTaxes={showAmountBeforeTaxes}
          isLoading={isPending}
          price={getBasePrice(priceType, sparePart)}
          onCancel={() => {
            state.priceUpdates.setUpdatesInProgress((prevState) =>
              prevState.filter((update) => update.sparePartId !== sparePart.id)
            );
          }}
          onConfirm={(newValue) => {
            updateSparePart({
              [updateField]:
                newValue !== undefined && !isNaN(newValue)
                  ? {
                      [updateAmountField]: newValue,
                    }
                  : null,
            });
          }}
          hasError={hasIsUpdatingError}
        />
      )}
    </Stack>
  );
};

const ActionSparePartReadPrice = ({
  showAmountBeforeTaxes,
  price,
  disabled,
  textSize,
  sparePart,
  edited = false,
  hasError = false,
}: {
  showAmountBeforeTaxes: boolean;
  price?: PriceAmountWithDetails;
  disabled: boolean;
  unit?: I18nWording | null;
  textSize: 'medium' | 'small';
  sparePart: ArticleActionWithRelations['spareParts'][number];
  edited: boolean;
  hasError: boolean;
}) => {
  const size = textSize === 'small' ? '300' : '200';
  const color = hasError ? 'danger' : disabled ? 'disabled' : 'secondary';

  if (!price) {
    return (
      <InlineText size={size} color={color}>
        -
      </InlineText>
    );
  }

  return (
    <InlineText size={size} color={color} nowrap>
      <PriceWithDetailsTooltipWithConversions
        price={price}
        variant="secondary"
        size="x-small"
        options={{
          showAmountBeforeTaxes: showAmountBeforeTaxes,
          showTaxDetails: true,
          sparePartPricesForBase: [
            {
              price,
              sparePart: { ...sparePart, quantity: 1 },
            },
          ],
        }}
        style={{
          textDecoration: disabled ? 'line-through' : '',
          color: edited ? 'var(--color-tertiary-700)' : hasError ? 'var(--color-danger-700)' : '',
        }}
      />
    </InlineText>
  );
};

const ActionSparePartEditPrice = ({
  showAmountBeforeTaxes,
  price,
  onCancel,
  onConfirm,
  isLoading,
  hasError,
}: {
  showAmountBeforeTaxes: boolean;
  price?: NullablePriceAmount | null;
  onCancel: () => void;
  onConfirm: (newValue: number | undefined) => void;
  isLoading?: boolean;
  hasError?: boolean;
}) => {
  const { t } = useLingui();
  const amount = showAmountBeforeTaxes ? price?.amountBeforeTaxes : price?.amount;
  const [value, setValue] = useState(amount ?? undefined);

  return (
    <Stack row gap="8px" alignItems="center">
      <InputMoney
        ariaLabel={t({
          id: 'spare-parts-table.price.amount.label',
          message: 'Amount',
        })}
        size="small"
        placeholder="0"
        style={{ maxWidth: '5rem', height: '2rem' }}
        value={value}
        currency={price?.currency ?? 'EUR'}
        onChange={setValue}
        isInvalid={hasError}
      />
      <Button
        ariaLabel={t({
          id: 'spare-parts-table.price.cancel',
          message: 'Cancel',
        })}
        size="small"
        variant="secondary"
        onPress={onCancel}
        tooltip={t({
          id: 'spare-parts-table.price.cancel',
          message: 'Cancel',
        })}
        disabled={isLoading}
        iconOnly
      >
        <IconCross />
      </Button>
      <Button
        ariaLabel={t({
          id: 'spare-parts-table.price.confirm',
          message: 'Confirm',
        })}
        tooltip={t({
          id: 'spare-parts-table.price.confirm',
          message: 'Confirm',
        })}
        size="small"
        variant="primary"
        onPress={() => onConfirm(value)}
        isLoading={isLoading}
        iconOnly
      >
        <IconCheck />
      </Button>
    </Stack>
  );
};
